import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PLACEHOLDER_IMAGE from '../../utils/placeholderImage';

/**
 * Horizontal, scroll-snap image strip — mobile-first with peek of adjacent slides.
 * @param {{ url: string, alt?: string }[] | string[]} images
 * @param {string} [title] — section label
 * @param {string} [productName]
 * @param {(url: string, alt: string) => void} [onImageClick]
 * @param {boolean} [hideIfEmpty]
 */
export default function ProductImageSlider({
  images,
  title,
  productName = '',
  onImageClick,
  hideIfEmpty = true,
}) {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  const normalized = React.useMemo(() => {
    if (!images || !images.length) return [];
    return images.map((item, i) => {
      if (typeof item === 'string') {
        return { url: item, alt: `${productName} — ${i + 1}` };
      }
      return {
        url: item.url,
        alt: item.alt || `${productName} — ${i + 1}`,
      };
    });
  }, [images, productName]);

  const n = normalized.length;

  const updateActiveFromScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !n) return;
    const slides = el.querySelectorAll('[data-slide]');
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    slides.forEach((slide, i) => {
      const mid = slide.offsetLeft + slide.offsetWidth / 2;
      if (mid <= center + 40) best = i;
    });
    setActive(best);
  }, [n]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateActiveFromScroll();
    el.addEventListener('scroll', updateActiveFromScroll, { passive: true });
    return () => el.removeEventListener('scroll', updateActiveFromScroll);
  }, [updateActiveFromScroll, n]);

  useEffect(() => {
    setActive(0);
    const el = scrollerRef.current;
    if (el) el.scrollTo({ left: 0, behavior: 'auto' });
  }, [normalized]);

  const scrollTo = useCallback((i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slides = el.querySelectorAll('[data-slide]');
    const target = slides[i];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    setActive(i);
  }, []);

  const prev = () => scrollTo(Math.max(0, active - 1));
  const next = () => scrollTo(Math.min(n - 1, active + 1));

  if (hideIfEmpty && n === 0) return null;

  const btnBase = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label={title || 'Product images'}
      style={{ marginBottom: 28 }}
    >
      {title ? (
        <h2
          style={{
            color: '#e5e7eb',
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            marginBottom: 14,
            textTransform: 'uppercase',
            opacity: 0.92,
          }}
        >
          {title}
        </h2>
      ) : null}

      <div style={{ position: 'relative' }}>
        {n > 1 && (
          <button
            type="button"
            aria-label="Previous slide"
            onClick={prev}
            disabled={active === 0}
            style={{
              ...btnBase,
              left: 4,
              opacity: active === 0 ? 0.35 : 1,
              cursor: active === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            <ChevronLeft size={22} aria-hidden />
          </button>
        )}

        <div
          ref={scrollerRef}
          className="product-image-slider-track"
          style={{
            display: 'flex',
            gap: 12,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'thin',
            padding: '4px 48px 12px',
            margin: '0 -12px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {(n === 0 ? [{ url: null, alt: productName }] : normalized).map((item, i) => (
            <div
              key={`${item.url || 'ph'}-${i}`}
              data-slide
              style={{
                flex: '0 0 min(88vw, 440px)',
                maxWidth: '100%',
                scrollSnapAlign: 'center',
              }}
            >
              {item.url ? (
                <img
                  src={item.url}
                  alt={item.alt}
                  role={onImageClick ? 'button' : undefined}
                  tabIndex={onImageClick ? 0 : undefined}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    objectFit: 'cover',
                    borderRadius: 16,
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.04)',
                    cursor: onImageClick ? 'zoom-in' : 'default',
                    display: 'block',
                  }}
                  loading="lazy"
                  onClick={() => onImageClick?.(item.url, item.alt)}
                  onKeyDown={
                    onImageClick
                      ? (ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            onImageClick(item.url, item.alt);
                          }
                        }
                      : undefined
                  }
                  onError={(ev) => {
                    if (ev.target.src !== PLACEHOLDER_IMAGE) ev.target.src = PLACEHOLDER_IMAGE;
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px dashed rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    fontSize: 14,
                  }}
                >
                  No image yet
                </div>
              )}
            </div>
          ))}
        </div>

        {n > 1 && (
          <button
            type="button"
            aria-label="Next slide"
            onClick={next}
            disabled={active >= n - 1}
            style={{
              ...btnBase,
              right: 4,
              opacity: active >= n - 1 ? 0.35 : 1,
              cursor: active >= n - 1 ? 'not-allowed' : 'pointer',
            }}
          >
            <ChevronRight size={22} aria-hidden />
          </button>
        )}
      </div>

      {n > 0 && (
        <p
          style={{
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 13,
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          {active + 1} / {n}
          <span style={{ marginLeft: 8, opacity: 0.85 }}>Swipe or use arrows</span>
        </p>
      )}
    </section>
  );
}
